import { isNil, upperFirst } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Frequency, RRule, Options as RRuleOptions } from 'rrule';
import { Menu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { Batch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import { Divider } from '@shared/scenes/workflows/edit/components/optionsMenu/divider';
import { Colors, grid } from '@shared/styles';
import { PM60, useFeatureFlag } from '@shared/util/featureFlags';
import { i18n } from '@shared/util/i18n';
import { AutoExtendOptionsDesktop } from './AutoExtendOptionsDesktop';
import { CustomBatch } from './CustomBatch';
import { EndDatePickerCalendar } from './EndDatePickerCalendar';
import { OffsetOptions } from './OffsetOptions';
import { useBatchSchedule } from './hooks/useBatchSchedule';
import { useIsNeverEnabled } from './hooks/useIsNeverEnabled';
import { AutoExtendConditionType, BatchScheduleComponentProps, RepeatEndType } from './types';
import {
  MAX_COUNT,
  MIN_COUNT,
  frequencyLabels,
  getAllDates,
  getInitialAutoExtendConditionType,
  getRruleText,
} from './utils';
import cssStyles from './BatchScheduleDesktop.module.css';

export function BatchScheduleDesktop({
  date,
  rruleOptions,
  setRruleOptions,
  presets,
  filteredPresets,
  count,
  setCount,
  until,
  setUntil,
  repeatEndType,
  setRepeatEndType,
  presetIndexSelected,
  setPresetIndexSelected,
  isCustomSelected,
  isOffsetSelected,
  setUserIsEditing,
  minimumInSeries,
  setMinimumInSeries,
  tagFilter,
  setTagFilter,
  component,
  workflowTemplateId,
  offset,
  setOffset,
  recurrenceType,
  setRecurrenceType,
}: BatchScheduleComponentProps) {
  const isPM60Enabled = useFeatureFlag(PM60);
  const isNeverEndTypeEnabled = useIsNeverEnabled({ minimumInSeries, tagFilter });
  const [conditionType, setConditionType] = useState<AutoExtendConditionType>(
    getInitialAutoExtendConditionType({
      minimumInSeries,
      tagFilter,
      component,
    }),
  );

  // Track the previous `conditionType` to ensure we only react to actual changes.
  const prevConditionTypeRef = useRef<AutoExtendConditionType>(conditionType);

  useEffect(() => {
    // Bail early if `conditionType` hasn't actually changed.
    if (prevConditionTypeRef.current === conditionType) {
      return;
    }

    prevConditionTypeRef.current = conditionType;

    if (conditionType === 'default') {
      setMinimumInSeries(component?.autoExtendDefault?.minimumInSeries ?? MAX_COUNT);
      setTagFilter({
        componentId: component?.autoExtendDefault?.impliedFilterComponentId ?? '',
        value: {
          containsString: null,
          isEmpty: null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      });
    } else if (conditionType === 'custom') {
      setMinimumInSeries(MAX_COUNT);
      setTagFilter(undefined);
    }
  }, [conditionType, minimumInSeries, tagFilter, component, setMinimumInSeries, setTagFilter]);

  const {
    handleCountChange,
    handleFreqSelect,
    handleOffsetSelect,
    handlePresetSelect,
    handleCustomSelect,
  } = useBatchSchedule({
    date,
    count,
    setCount,
    setPresetIndexSelected,
    setRruleOptions,
    presets,
    setUserIsEditing,
    setOffset,
    setRecurrenceType,
  });

  const countIsOutOfRange = !isNil(count) && (count < MIN_COUNT || count > MAX_COUNT);
  const showPresetOptions = recurrenceType === 'RRULE_SCHEDULE' && !isCustomSelected;
  const showCustomOptions = recurrenceType === 'RRULE_SCHEDULE' && isCustomSelected;
  const showOffsetOptions = recurrenceType === 'OFFSET';
  const showAutoExtendOptions = isPM60Enabled && recurrenceType === 'RRULE_SCHEDULE';
  const showEndMenu = recurrenceType === 'RRULE_SCHEDULE';

  return (
    <>
      <Divider vertical />
      <View style={{ width: grid(37) }} gap={1} paddingVertical={1.5}>
        <FrequencyMenu
          rruleOptions={rruleOptions}
          isCustomSelected={isCustomSelected}
          isOffsetSelected={isOffsetSelected}
          handleOffsetSelect={handleOffsetSelect}
          handleFreqSelect={handleFreqSelect}
          handleCustomSelect={handleCustomSelect}
        />
        {showPresetOptions && (
          <PresetOptions
            presets={presets}
            filteredPresets={filteredPresets}
            presetIndexSelected={presetIndexSelected}
            handlePresetSelect={handlePresetSelect}
          />
        )}
        {showCustomOptions && (
          <CustomBatch
            rruleOptions={rruleOptions}
            setRruleOptions={setRruleOptions}
            setUserIsEditing={setUserIsEditing}
          />
        )}
        {showOffsetOptions && (
          <OffsetOptions
            offset={offset}
            setOffset={setOffset}
            tagFilter={tagFilter}
            setTagFilter={setTagFilter}
            workflowTemplateId={workflowTemplateId}
          />
        )}
        {showEndMenu && (
          <EndMenu
            repeatEndType={repeatEndType}
            count={count}
            handleCountChange={handleCountChange}
            until={until}
            countIsOutOfRange={countIsOutOfRange}
            rruleOptions={rruleOptions}
            setUntil={setUntil}
            setUserIsEditing={setUserIsEditing}
            setRepeatEndType={setRepeatEndType}
            includeNever={isNeverEndTypeEnabled}
          />
        )}
        {showAutoExtendOptions && (
          <AutoExtendOptionsDesktop
            minimumInSeries={minimumInSeries}
            setMinimumInSeries={setMinimumInSeries}
            tagFilter={tagFilter}
            setTagFilter={setTagFilter}
            component={component}
            workflowTemplateId={workflowTemplateId}
            conditionType={conditionType}
            setConditionType={setConditionType}
            setRepeatEndType={setRepeatEndType}
          />
        )}
      </View>
    </>
  );
}

function FrequencyMenu({
  rruleOptions,
  isCustomSelected,
  isOffsetSelected,
  handleOffsetSelect,
  handleFreqSelect,
  handleCustomSelect,
}: {
  isCustomSelected: boolean;
  isOffsetSelected: boolean;
  handleOffsetSelect: () => void;
  handleFreqSelect: (newFreq: Frequency) => void;
  handleCustomSelect: () => void;
} & Pick<Batch, 'rruleOptions'>) {
  const freqKeys = Object.keys(frequencyLabels).sort((a, b) => Number(b) - Number(a));
  const scheduleLabel = isCustomSelected
    ? i18n('workflowTemplate.component.dateAndRepeat.custom')
    : frequencyLabels[rruleOptions?.freq ?? RRule.WEEKLY];
  const label = isOffsetSelected ? 'One at a time' : scheduleLabel;

  return (
    <View row centerVertically paddingHorizontal={2} gap={1}>
      <Text size="body1" weight="semibold">
        {i18n('workflowTemplate.component.dateAndRepeat.repeats')}
      </Text>
      <Menu>
        <Menu.Button flex label={label} />
        <Menu.Content>
          <Menu.Item
            onClick={handleOffsetSelect}
            label={i18n('workflowTemplate.component.dateAndRepeat.offset.oneAtATimeTitle')}
            endContent={
              isOffsetSelected ? (
                <div style={{ display: 'flex', flexBasis: grid(3), marginRight: -grid(1) }}>
                  <MBIconV2 color={Colors.v2.green} name="check-box" size={grid(3)} />
                </div>
              ) : undefined
            }
            className={cssStyles.item}
          />
          <Menu.Separator />
          <Menu.Section title={i18n('workflowTemplate.component.dateAndRepeat.onASchedule')} />
          {freqKeys.map(freqType => (
            <Menu.Item
              key={freqType}
              label={frequencyLabels[freqType]}
              onClick={() => handleFreqSelect(Number(freqType))}
              endContent={
                rruleOptions?.freq === Number(freqType) && !isCustomSelected ? (
                  <div style={{ display: 'flex', flexBasis: grid(3), marginRight: -grid(1) }}>
                    <MBIconV2 color={Colors.v2.green} name="check-box" size={grid(3)} />
                  </div>
                ) : undefined
              }
              className={cssStyles.item}
            />
          ))}
          <Menu.Item
            label={i18n('workflowTemplate.component.dateAndRepeat.custom')}
            onClick={handleCustomSelect}
            endContent={
              isCustomSelected ? (
                <div style={{ display: 'flex', flexBasis: grid(3), marginRight: -grid(1) }}>
                  <MBIconV2 color={Colors.v2.green} name="check-box" size={grid(3)} />
                </div>
              ) : undefined
            }
            className={cssStyles.item}
          />
        </Menu.Content>
      </Menu>
    </View>
  );
}

function PresetOptions({
  presets,
  filteredPresets,
  presetIndexSelected,
  handlePresetSelect,
}: {
  presets: Partial<RRuleOptions>[];
  filteredPresets: Partial<RRuleOptions>[];
  presetIndexSelected: number;
  handlePresetSelect: (preset: Partial<RRuleOptions>, index: number) => void;
}) {
  return (
    <View style={{ width: '100%' }} paddingHorizontal={1.5}>
      {filteredPresets.map((preset, index) => {
        const presetWithoutCountAndUntil = { ...preset, count: undefined, until: undefined };
        const rruleText = getRruleText(presetWithoutCountAndUntil);

        return (
          <div
            key={rruleText}
            onClick={() => handlePresetSelect(preset, index)}
            onKeyPress={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                handlePresetSelect(preset, index);
              }
            }}
            tabIndex={0}
            style={{
              paddingTop: grid(0.5),
              paddingBottom: grid(0.5),
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 1,
              flexGrow: 0,
              width: '100%',
            }}
          >
            <View style={{ flexBasis: grid(3) }} marginRight={2}>
              {presetIndexSelected === index && (
                <>
                  {presets.length === 1 && (
                    <MBIconV2 color={Colors.v2.green} name="check-filled" size={grid(4)} />
                  )}
                  {presets.length > 1 && (
                    <MBIconV2 color={Colors.v2.green} name="radio-button" size={grid(4)} />
                  )}
                </>
              )}
              {presetIndexSelected !== index && (
                <MBIconV2 color={Colors.v2.green} name="unfilled-circle" size={grid(4)} />
              )}
            </View>
            <div style={{ width: 'calc(100% - 40px)', lineHeight: 1.43 }}>
              {upperFirst(rruleText)}
            </div>
          </div>
        );
      })}
    </View>
  );
}

interface EndMenuProps {
  repeatEndType: RepeatEndType;
  count?: number;
  handleCountChange: (newCount: string) => void;
  until?: Date;
  countIsOutOfRange: boolean;
  rruleOptions?: Partial<RRuleOptions>;
  setUntil?: (newUntil?: Date) => void;
  setUserIsEditing: (isEditing: boolean) => void;
  setRepeatEndType: (newRepeatEndType: RepeatEndType) => void;
  includeNever: boolean;
}

function EndMenu({
  repeatEndType,
  count,
  handleCountChange,
  until,
  countIsOutOfRange,
  rruleOptions,
  setUntil,
  setUserIsEditing,
  setRepeatEndType,
  includeNever,
}: EndMenuProps) {
  const handleRepeatEndTypeChange = (newRepeatEndType: RepeatEndType) => {
    setRepeatEndType(newRepeatEndType);
    setUserIsEditing(true);
  };

  const handleUntilChange = (newUntil: Date) => {
    setUntil?.(newUntil);
    setUserIsEditing(true);
  };

  // Calculate a fallback date (furthest date within the first MAX_COUNT occurrences)
  const fallbackDate = useMemo(() => {
    const allDates = getAllDates({ rruleOptions });
    return allDates.length ? allDates[allDates.length - 1] : new Date();
  }, [rruleOptions]);

  const buttonLabel = (() => {
    switch (repeatEndType) {
      case RepeatEndType.COUNT:
        return i18n('workflowTemplate.component.dateAndRepeat.after');
      case RepeatEndType.END_DATE:
        return i18n('workflowTemplate.component.dateAndRepeat.on');
      case RepeatEndType.NEVER:
        return i18n('workflowTemplate.component.dateAndRepeat.never');
      default:
        return '';
    }
  })();

  return (
    <View gap={1}>
      <View row style={{ justifyContent: 'flex-start' }} paddingHorizontal={2} gap={1}>
        <View centerVertically>
          <Text size="body1" weight="semibold">
            {i18n('workflowTemplate.component.dateAndRepeat.ends')}
          </Text>
        </View>
      </View>
      <View row style={{ justifyContent: 'flex-start' }} paddingHorizontal={2} gap={1}>
        <Menu>
          <Menu.Button label={buttonLabel} />
          <Menu.Content>
            <Menu.Item
              label={i18n('workflowTemplate.component.dateAndRepeat.after')}
              onClick={() => handleRepeatEndTypeChange(RepeatEndType.COUNT)}
            />
            <Menu.Item
              label={i18n('workflowTemplate.component.dateAndRepeat.on')}
              onClick={() => handleRepeatEndTypeChange(RepeatEndType.END_DATE)}
            />
            {includeNever && (
              <Menu.Item
                label={i18n('workflowTemplate.component.dateAndRepeat.never')}
                onClick={() => handleRepeatEndTypeChange(RepeatEndType.NEVER)}
              />
            )}
          </Menu.Content>
        </Menu>
        {repeatEndType === RepeatEndType.COUNT && (
          <>
            <MBTextInput
              value={String(count ?? '')}
              onChangeText={handleCountChange}
              style={[styles.input, countIsOutOfRange && styles.error]}
            />
            <View centerVertically>
              <Text size="body1">{i18n('workflowTemplate.component.dateAndRepeat.times')}</Text>
            </View>
          </>
        )}
        {repeatEndType === RepeatEndType.END_DATE && (
          <EndDatePicker
            until={until}
            fallbackDate={fallbackDate}
            onSelect={handleUntilChange}
            minDate={rruleOptions?.dtstart as Date}
            rruleOptions={rruleOptions}
          />
        )}
      </View>
    </View>
  );
}

interface EndDatePickerProps {
  until: Date | undefined;
  fallbackDate?: Date;
  onSelect: (newDate: Date) => void;
  minDate?: Date;
  rruleOptions?: Partial<RRuleOptions>;
}

function EndDatePicker({
  until,
  fallbackDate,
  onSelect,
  minDate,
  rruleOptions,
}: EndDatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedDate = useMemo(() => until ?? fallbackDate ?? new Date(), [until, fallbackDate]);

  const handleSelect = (date: Date) => {
    const dtstartDate = moment(rruleOptions?.dtstart);
    const dtstartHour = dtstartDate.hour();
    const dtstartMinute = dtstartDate.minute();
    const dtstartSecond = dtstartDate.second();
    const dtstartMillisecond = dtstartDate.millisecond();

    const untilDate = moment(date);
    untilDate.hour(dtstartHour);
    untilDate.minute(dtstartMinute);
    untilDate.second(dtstartSecond);
    untilDate.millisecond(dtstartMillisecond);

    onSelect(untilDate.toDate());
    setIsOpen(false);
  };

  return (
    <Menu isOpen={isOpen} onOpenChange={setIsOpen}>
      <Menu.Button flex label={moment(selectedDate).format('MMM D, YYYY')} />
      <Menu.Content style={{ padding: grid(1), minWidth: grid(36) }}>
        <EndDatePickerCalendar
          selectedDate={selectedDate}
          minDate={minDate}
          rruleOptions={rruleOptions}
          onSelect={handleSelect}
        />
      </Menu.Content>
    </Menu>
  );
}

const styles = StyleSheet.create({
  input: {
    width: grid(6),
    borderWidth: 1,
    borderColor: Colors.v2.gray10,
    borderStyle: 'solid',
    borderRadius: grid(1),
  },
  error: {
    borderColor: Colors.v2.red,
  },
});
